import { useState, useContext, useEffect} from "react";
import moment from 'moment-timezone';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { Popper } from '@mui/base/Popper';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { Box, Grid, ListItemButton, ListItemText, SvgIcon, Stack, Typography } from '@mui/material'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { 
    dropdownIconPath
  } from '../common/iconPaths'
import { DateView } from "@mui/x-date-pickers";
import { DataContext, DataDispatchContext } from '../../contexts/data/dataContext'
import { useGetCurrentSession } from '../../services/api-query/auth'
import { 
    useGetReportDailyData,
    useGetLastFewDaysReportData,
    useGetReportMonthlyData,
    useGetBatteryDailyData 
} from '../../services/api-query/reportData'
import custom from '../../styles/mui/szVariables.json'
import { analyticsDomain, analyticsCta, analyticsPage } from '../../services/constants'
import { sendClickAnalytics, sendViewAnalytics, getPageName } from '../common/util/util'

enum DATEGROUP {
    day,
    month,
    year
}
const month = moment.monthsShort()

export default function ReportDateFilter({ id, setCsvData }: any) {
    const data = useContext(DataContext)
    const dispatch: any = useContext(DataDispatchContext)
    const border = custom.border;
    const timezone = data?.customer?.account?.timezone || 'Pacific/Auckland'
    const formatStr = 'ddd DD MMM YYYY'
    const [selectedFilter, setSelectedFilter] = useState({
        group: DATEGROUP.day,
        day: moment.tz(timezone),
        month: moment.tz(timezone),
        year: moment.tz(timezone)
    });    
    const { currentSession } = useGetCurrentSession()
    const { reportsData } = useGetLastFewDaysReportData(currentSession)
    const { reportPending, dailyData } = useGetReportDailyData(
            selectedFilter.day.format(),
            currentSession
        )
    const { dailyBatteryData } = useGetBatteryDailyData(
        selectedFilter.day.format(),
        currentSession
    )
    const { monthlyData } = useGetReportMonthlyData(
        selectedFilter.month.startOf('month').format(),
        currentSession
    )
    const [selectedGroup, setSelectedGroup] = 
        useState(moment.tz(timezone).format(formatStr));
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [views, setViews] = useState(['day' as DateView]);
    const [arrowColor, setArrowColor] = useState({ 
                                            left: 'text.primary', 
                                            right: 'sz.colors.gray100'
                                        } as any);

    // first usage data day    
    const firstUsageDate = data?.customer?.firstUsage?.day || '2023-11-01'
    const lastDataDay = moment(firstUsageDate).tz(timezone)
    
    useEffect(() => {
        if(!reportPending) {
            setArrowColor({
                left: 'text.primary',
                right: 'text.primary'
            })
        }
        if(selectedFilter.group===DATEGROUP.day) {
            selectedFilter.month = selectedFilter.day?.clone()
            const today = moment().format('YYYYMMDD');
            if(selectedFilter.day?.format('YYYYMMDD') === today) {
                setArrowColor({
                    left: 'text.primary',
                    right: 'sz.colors.gray100'
                })
            }
            if(selectedFilter.day?.startOf('day')
                    .isSame(lastDataDay.startOf('day'))) {
                setArrowColor({
                    left: 'sz.colors.gray100',
                    right: 'text.primary',
                })
            }
            const reportDailyData = (reports = []) =>{
                return reports.find((r:any)=>
                                    r.key===selectedFilter.day.format('YYYYMMDD')
                                )
            }
            if(dailyData && reportsData && currentSession) {
                let selectedReportData: any = reportDailyData(reportsData?.reports);
                if(!selectedReportData) {
                    selectedReportData = reportDailyData(dailyData?.reports);
                }
                if(selectedReportData) {
                    selectedReportData.batteryStateOfCharge = dailyBatteryData?.stateOfCharge;
                    dispatch({
                        type: 'setReportData',
                        selectedReportData,
                    })
                }
            }
        }
        if(selectedFilter.group===DATEGROUP.month) {
            if(selectedFilter.day?.format('MMYYYY') !== selectedFilter.month?.format('MMYYYY')) {
                selectedFilter.day = selectedFilter.month?.clone().startOf('month')
            }
            const today = moment().format('YYYYMM');
            if(selectedFilter.month?.format('YYYYMM') === today) {
                setArrowColor({
                    left: 'text.primary',
                    right: 'sz.colors.gray100'
                })
            }
            if(selectedFilter.month?.startOf('day')
                .isSame(lastDataDay.startOf('day'))) {
            setArrowColor({
                left: 'sz.colors.gray100',
                right: 'text.primary',
            })
        }
            if(monthlyData) {
                const selectedReportData = monthlyData?.reports[0]
                if(selectedReportData) {
                    dispatch({
                        type: 'setReportData',
                        selectedReportData,
                    })
                }
            }
        }
        setCsvData(data?.prepareCsvData(monthlyData?.reports[0]));
    },[
        dailyData,
        dailyBatteryData,
        monthlyData,
        reportsData,
        dispatch, 
        selectedFilter,
        selectedGroup, 
        currentSession,
        timezone,
        reportPending 
    ])

    const handleDateGrouping = (group: number)=>{
        setSelectedFilter({...selectedFilter, group})
        if(group === DATEGROUP.day) {
            clickAnalytics(analyticsCta.trendsSelectDaily)
            const name = getPageName(id, 'hourly')
            sendViewAnalytics('/performance', {
                domain: analyticsDomain.trends,
                name 
            })
            setSelectedGroup(selectedFilter.day.tz(timezone).format(formatStr))
        }
        if(group === DATEGROUP.month) {
            clickAnalytics(analyticsCta.trendsSelectMonthly)
            const name = getPageName(id, 'daily')
            sendViewAnalytics('/performance', {
                domain: analyticsDomain.trends,
                name 
            })
            setSelectedGroup(
                `${month[selectedFilter.month.month()]} ${selectedFilter.month.year()}`
            )
        }
        if(group === DATEGROUP.year) {
            clickAnalytics(analyticsCta.trendsSelectYearly)
            const name = getPageName(id, 'monthly')
            sendViewAnalytics('/performance', {
                domain: analyticsDomain.trends,
                name 
            })
            setSelectedGroup(`${selectedFilter.year.year()}`)
        }
    }

    const handleLeftValue = () => {
        if(reportPending) {
            setArrowColor({
                left: 'sz.colors.gray100',
                right: 'sz.colors.gray100'
            })
        } else {
            setArrowColor({
                left: 'text.primary',
                right: 'text.primary'
            })
            let newValue = {...selectedFilter }
            if(selectedFilter.group===DATEGROUP.day) {
                clickAnalytics(analyticsCta.trendsPreviousDay)
                const leftValue = selectedFilter.day.clone().subtract(1, 'day');
                if(leftValue.tz(timezone).startOf('day') >= lastDataDay.startOf('day')) {
                    setSelectedGroup(leftValue.tz(timezone).format(formatStr))
                    newValue = {...selectedFilter, day: leftValue }
                    setArrowColor({...arrowColor, right: 'auto'})
                } else {
                    setArrowColor({
                        left: 'sz.colors.gray100',
                        right: 'text.primary'
                    })
                } 
            }
            if(selectedFilter.group===DATEGROUP.month) {
                clickAnalytics(analyticsCta.trendsPreviousMonth)
                const leftValue = selectedFilter.month.clone().subtract(1,'month')
                if(leftValue.tz(timezone).startOf('month') >= lastDataDay.startOf('month')) {
                    setSelectedGroup(`${month[leftValue.month()]} ${leftValue.year()}`)
                    newValue = {...selectedFilter, month: leftValue }
                    setArrowColor({...arrowColor, right: 'auto'})
                } else {
                    setArrowColor({
                        left: 'sz.colors.gray100',
                        right: 'text.primary'
                    })
                } 
            }
            if(selectedFilter.group===DATEGROUP.year) {
                clickAnalytics(analyticsCta.trendsPreviousYear)
                const leftValue = selectedFilter.year.clone().subtract(1,'year')
                if(leftValue.tz(timezone).startOf('year') >= lastDataDay.startOf('year')) {
                    setSelectedGroup(`${leftValue.year()}`)
                    newValue = {...selectedFilter, year: leftValue }
                    setArrowColor({...arrowColor, right: 'auto'})
                } else {
                    setArrowColor({
                        left: 'sz.colors.gray100',
                        right: 'text.primary'
                    })
                } 
            }
            
            setSelectedFilter(newValue)
        }
    }

    const handleRightValue = () => {
        if(reportPending) {
            setArrowColor({
                left: 'sz.colors.gray100',
                right: 'sz.colors.gray100'
            })
        } else {
            setArrowColor({
                left: 'text.primary',
                right: 'text.primary'
            })
            let newValue = {...selectedFilter }
            if(selectedFilter.group===DATEGROUP.day) {
                clickAnalytics(analyticsCta.trendsNextDay)
                const rightValue = selectedFilter.day.clone().add(1,'day')
                if(rightValue.tz(timezone).endOf('day') <= moment().tz(timezone).endOf('day')) {
                    setSelectedGroup(rightValue.tz(timezone).format(formatStr))
                    newValue = {...selectedFilter, day: rightValue }
                    setArrowColor({...arrowColor, left: 'auto'})
                } else {
                    setArrowColor({
                        right: 'sz.colors.gray100',
                        left: 'text.primary'
                    })
                } 
            }
            if(selectedFilter.group===DATEGROUP.month) {
                clickAnalytics(analyticsCta.trendsNextMonth)
                const rightValue = selectedFilter.month.clone().add(1,'month')
                if(rightValue.endOf('month') <= moment().endOf('month')) {
                    setSelectedGroup(`${month[rightValue.month()]} ${rightValue.year()}`)
                    newValue = {...selectedFilter, month: rightValue }
                } else {
                    setArrowColor({
                        right: 'sz.colors.gray100',
                        left: 'text.primary'
                    })
                } 
            
            }
            if(selectedFilter.group===DATEGROUP.year) {
                clickAnalytics(analyticsCta.trendsNextYear)
                const rightValue = selectedFilter.year.clone().add(1,'year')
                if(rightValue.endOf('year') <= moment().endOf('year')) {
                    setSelectedGroup(`${rightValue.year()}`)
                    newValue = {...selectedFilter, year: rightValue }
                }  
            }
            setSelectedFilter(newValue)
        }
    }

    const handleDatePiker = (event: any) =>{
        if(selectedFilter.group===DATEGROUP.month) { 
            setViews(['month','year'])
        }
        if(selectedFilter.group===DATEGROUP.year) { 
            setViews(['year'])
        }
        if(selectedFilter.group===DATEGROUP.day) { 
            setViews(['day'])
        }
        setAnchorEl(anchorEl ? null : event.currentTarget);
        
    }

    const handlePickerValue = (newDate: Dayjs) => {
        setAnchorEl(null)
        let newValue = {...selectedFilter }
        const newMoment= moment(newDate.valueOf())
        if(selectedFilter.group===DATEGROUP.day) {
            clickAnalytics(analyticsCta.trendsCalendarDay)
            setSelectedGroup(newMoment.tz(timezone).format(formatStr))    
            newValue = {...selectedFilter, day: newMoment }        
        }
        if(selectedFilter.group===DATEGROUP.month) {
            clickAnalytics(analyticsCta.trendsCalendarMonth)
            setSelectedGroup(`${month[newMoment.month()]} ${newMoment.year()}`) 
            newValue = {...selectedFilter, month: newMoment }
        }
        if(selectedFilter.group===DATEGROUP.year) {
            clickAnalytics(analyticsCta.trendsCalendarYear)
            setSelectedGroup(`${newMoment.year()}`)   
            newValue = {...selectedFilter, month: newMoment }
        }
        
        setSelectedFilter(newValue)
    }
    
    const clickAnalytics = (name: string, metadata={}) => {
        sendClickAnalytics('/performance', {
            domain: analyticsDomain.trends,
            name,
            metadata,
        })
      }

    const calendarValue = () => {
        let initial = dayjs(selectedFilter.day?.format())
        if(selectedFilter.group===DATEGROUP.month) {
            initial = dayjs(selectedFilter.month?.format())
        }
        if(selectedFilter.group===DATEGROUP.year) {
            initial = dayjs(selectedFilter.year?.format())
        }
        return initial
    }
    
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container direction="row" justifyContent={{
                 xs: "center",
                 sm: "space-between",
                 md: "space-between",
                 lg: "space-between",
                }} >
                <Grid item xs={12} sm={4} md={4} lg={4}
                    justifyContent= {{
                        xs: "center",
                        sm: "flex-start",
                        md: "flex-start",
                        lg: "flex-start",
                       }}
                    alignContent={"center"}
                    display={"flex"}
                    //   sx={{ borderBottom: border }} 
                      >
                    <Box display="flex" ml={custom.spacing.sm}
                        justifyContent={"flex-start"} 
                        maxWidth="200px" >
                        <ListItemButton id="day" 
                            onClick={()=>handleDateGrouping(DATEGROUP.day)} 
                            sx={{
                                borderBottom: 
                                    selectedFilter.group===DATEGROUP.day?
                                    custom.selected:'none' 
                            }}>
                            <ListItemText primary="Day" primaryTypographyProps={{ 
                                variant: 'szHeading.Regular.xSmall',
                                color: selectedFilter.group===DATEGROUP.day?
                                        'primary.main':'inherit',
                            }}/>
                        </ListItemButton >
                        <ListItemButton id="month" 
                            onClick={()=>handleDateGrouping(DATEGROUP.month)} 
                            sx={{
                                borderBottom: 
                                    selectedFilter.group===DATEGROUP.month?
                                    custom.selected:'none' 
                            }}>
                            <ListItemText primary="Month" 
                                primaryTypographyProps={{            
                                variant: 'szHeading.Regular.xSmall',
                                color: selectedFilter.group===DATEGROUP.month?
                                        'primary.main':'inherit', 
                            }}/>
                        </ListItemButton>
                        <ListItemButton id="year" 
                                onClick={()=>handleDateGrouping(DATEGROUP.year)} 
                                sx={{
                                borderBottom: 
                                    selectedFilter.group===DATEGROUP.year?
                                    custom.selected:'none' 
                            }}>
                            {/* <ListItemText primary="Year" 
                                primaryTypographyProps={{ 
                                variant: 'szHeading.Regular.xSmall' ,
                                color: selectedFilter.group===DATEGROUP.year?
                                        'primary.main':'inherit',
                            }}/> */}
                        </ListItemButton>
                    </Box>
                </Grid>
                <Grid item justifyContent="flex-end">
                    <Stack  alignItems="start" direction="row" 
                            justifyContent="flex-end"
                            py={custom.spacing.xxs}>
                        <KeyboardArrowLeftIcon sx={{ 
                                borderRadius: custom.corners6px, 
                                border,  
                                height: '32px',
                                width: '32px',
                                p: custom.spacing.xxxs,
                                cursor: 'pointer', 
                                color:  arrowColor.left
                            }} 
                            id="previousDayButton"
                            onClick={handleLeftValue}/>
                        <Stack alignItems="start" direction="row" 
                                justifyContent="flex-center" 
                                sx={{ 
                                    borderRadius: custom.corners6px, 
                                    border,
                                    px: custom.spacing.xs,
                                    py: custom.spacing.xxs,
                                    mx: custom.spacing.xxs,
                                }}
                                >
                            <Typography noWrap={true} 
                                variant="szBody.Regular.Medium" 
                                sx={{ cursor: 'pointer' }} 
                                id="dayText"
                                onClick={handleDatePiker}>
                                    {selectedGroup}
                            </Typography>
                            <SvgIcon sx={{ 
                                    cursor: 'pointer', 
                                    pl: custom.spacing.xxs
                                }} 
                                id="dayIcon"
                                onClick={handleDatePiker}> 
                                {dropdownIconPath} 
                            </SvgIcon>
                        </Stack>
                        <KeyboardArrowRightIcon sx={{ 
                                borderRadius: custom.corners6px, 
                                border,  
                                height: '32px',
                                width: '32px',
                                p: custom.spacing.xxxs,
                                cursor: 'pointer', 
                                mr: custom.spacing.sm,
                                color: arrowColor.right
                            }} 
                            id="nextDayButton"
                            onClick={handleRightValue}/>
                    </Stack>
                </Grid>
            </Grid>
            <Popper id={"calendar"} open={Boolean(anchorEl)} anchorEl={anchorEl}  >
                <ClickAwayListener onClickAway={()=>setAnchorEl(null)}>
                    <Box m={custom.spacing.sm} 
                        sx={{ 
                            borderRadius: custom.corners4, 
                            background: '#FFF', 
                            border}}>
                        <DateCalendar disableFuture 
                            shouldDisableDate={(day: Dayjs)=>{
                                if(day.startOf('day') < lastDataDay.startOf('day')) {
                                    return true;
                                }
                                return false;
                            }}
                            shouldDisableMonth={(month: Dayjs)=>{
                                if(month.year() < lastDataDay.year()) {
                                    return true;
                                }
                                if( month.year() === lastDataDay.year() &&
                                    month.month() < lastDataDay.month()) {
                                    return true;
                                }
                                return false;
                            }}
                            shouldDisableYear={(year: Dayjs)=>{
                                if(year.year() < lastDataDay.year()) {
                                    return true;
                                }
                                return false;
                            }}
                            views={views} 
                            openTo={views[0]}
                            value={calendarValue()} 
                            onChange={handlePickerValue} />
                    </Box>
                </ClickAwayListener>
            </Popper>
       </LocalizationProvider>
    )
}